import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';

import { ALBUM_KIND, type AlbumMeta, parseAlbum } from '@/lib/album-utils';
import { getImageUrl } from '@/lib/polaroid-utils';

export interface AlbumData {
  /** Parsed album metadata. */
  album: AlbumMeta;
  /** Fetched kind:20 picture events referenced by the album. */
  pictures: NostrEvent[];
}

/**
 * Fetch a specific album by author pubkey and d-tag,
 * then batch-fetch the referenced kind:20 picture events.
 */
export function useAlbum(pubkey: string | undefined, dTag: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<AlbumData | undefined>({
    queryKey: ['albums', 'detail', pubkey ?? '', dTag ?? ''],
    queryFn: async () => {
      if (!pubkey || !dTag) return undefined;

      // Fetch the album event
      const albumEvents = await nostr.query(
        [{ kinds: [ALBUM_KIND], authors: [pubkey], '#d': [dTag], limit: 1 }],
        { signal: AbortSignal.timeout(5000) },
      );

      if (albumEvents.length === 0) return undefined;

      const album = parseAlbum(albumEvents[0]);
      if (!album) return undefined;

      // Fetch the referenced pictures
      if (album.photoIds.length === 0) {
        return { album, pictures: [] };
      }

      const pictureEvents = await nostr.query(
        [{ kinds: [20], ids: album.photoIds, limit: album.photoIds.length }],
        { signal: AbortSignal.timeout(5000) },
      );

      // Validate pictures and preserve album ordering
      const pictureMap = new Map<string, NostrEvent>();
      for (const event of pictureEvents) {
        if (event.kind === 20 && getImageUrl(event)) {
          pictureMap.set(event.id, event);
        }
      }

      // Return pictures in album order (order of e tags)
      const orderedPictures = album.photoIds
        .map((id) => pictureMap.get(id))
        .filter((e): e is NostrEvent => !!e);

      return { album, pictures: orderedPictures };
    },
    enabled: !!pubkey && !!dTag,
    staleTime: 60 * 1000,
  });
}
